import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import BACKEND_URL from '../../vnog/config';

type TaxonomyKind = 'phase' | 'role';

interface TaxonomyItem {
    id: number;
    slug: string;
    description: string | null;
    created_at: string | null;
    document_count: number;
}

interface DocumentSummary {
    id: number;
    name: string | null;
}

interface DocumentationPage {
    count: number;
    next: string | null;
    previous: string | null;
    results: Array<{ id: number; name?: string | null; summary?: string | null }>;
}

const KIND_LABEL: Record<TaxonomyKind, { singular: string; plural: string; pluralNl: string }> = {
    phase: { singular: 'phase', plural: 'phases', pluralNl: 'Phasen' },
    role: { singular: 'role', plural: 'roles', pluralNl: 'Rollen' },
};

const PAGE_SIZE = 25;

const apiBase = (kind: TaxonomyKind) => `${BACKEND_URL}/vnog/admin/${kind === 'phase' ? 'phases' : 'roles'}`;

// ---------------------------------------------------------------------------
// Inline document list — lazy fetched when a row is expanded.
// ---------------------------------------------------------------------------

const AssociatedDocuments: React.FC<{ kind: TaxonomyKind; itemId: number }> = ({ kind, itemId }) => {
    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);
    const [items, setItems] = useState<DocumentSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('page_size', String(PAGE_SIZE));
        const filterKey = kind === 'phase' ? 'phase_ids' : 'role_ids';
        params.set(filterKey, JSON.stringify([itemId]));
        setLoading(true);
        setError(null);
        fetch(`${BACKEND_URL}/vnog/get_all_documentation/?${params.toString()}`, {
            credentials: 'include',
        })
            .then(async (response) => {
                if (!response.ok) throw new Error(`status ${response.status}`);
                const data: DocumentationPage = await response.json();
                if (cancelled) return;
                const next: DocumentSummary[] = (data.results || []).map((r) => ({
                    id: r.id,
                    name: r.name ?? null,
                }));
                setCount(typeof data.count === 'number' ? data.count : next.length);
                setItems((prev) => (page === 1 ? next : [...prev, ...next]));
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                const msg = e instanceof Error ? e.message : 'Unknown error';
                setError(msg);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [kind, itemId, page]);

    const canLoadMore = items.length < count;

    return (
        <div className="mt-3 border-t border-gray-200 pt-3">
            {error && (
                <div className="text-sm text-red-600 mb-2">Failed to load documents: {error}</div>
            )}
            {loading && items.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">Loading documents...</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-gray-500 italic">No documents associated with this {kind}.</div>
            ) : (
                <>
                    <ul className="space-y-1 max-h-64 overflow-y-auto">
                        {items.map((doc) => (
                            <li key={doc.id} className="text-sm text-gray-800 flex items-center gap-2">
                                <span className="font-mono text-xs text-gray-400 w-12 shrink-0">#{doc.id}</span>
                                <span className="truncate">{doc.name || <em className="text-gray-400">(no name)</em>}</span>
                            </li>
                        ))}
                    </ul>
                    {canLoadMore && (
                        <button
                            onClick={() => setPage((p) => p + 1)}
                            disabled={loading}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                            {loading ? 'Loading…' : `Load more (${items.length} / ${count})`}
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// One row in the taxonomy list with inline edit + expand.
// ---------------------------------------------------------------------------

const TaxonomyRow: React.FC<{
    kind: TaxonomyKind;
    item: TaxonomyItem;
    onUpdated: (item: TaxonomyItem) => void;
}> = ({ kind, item, onUpdated }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(item.description || '');
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(`${apiBase(kind)}/${item.id}/update/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: JSON.stringify({ description: draft }),
            });
            const data = await response.json();
            if (!response.ok) {
                toast.error(data.error || `Failed to update ${kind}`);
                return;
            }
            onUpdated(data);
            setEditing(false);
            toast.success(`${KIND_LABEL[kind].singular[0].toUpperCase()}${KIND_LABEL[kind].singular.slice(1)} updated`);
        } catch {
            toast.error(`Error updating ${kind}`);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setDraft(item.description || '');
        setEditing(false);
    };

    return (
        <div className="border border-gray-200 rounded-md p-4">
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <code className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono text-gray-800">
                            {item.slug}
                        </code>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {item.document_count} document{item.document_count === 1 ? '' : 's'}
                        </span>
                        {item.created_at && (
                            <span className="text-xs text-gray-400">
                                {new Date(item.created_at).toLocaleDateString()}
                            </span>
                        )}
                    </div>
                    {editing ? (
                        <div className="mt-3 space-y-2">
                            <textarea
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                rows={10}
                                className="w-full px-3 py-2 text-sm leading-relaxed border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[12rem] font-sans"
                                placeholder="Description"
                                autoFocus
                            />
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {saving ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                    onClick={handleCancel}
                                    disabled={saving}
                                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <span className="ml-auto text-xs text-gray-400">
                                    {draft.length} character{draft.length === 1 ? '' : 's'}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {item.description || <em className="text-gray-400">No description</em>}
                        </div>
                    )}
                </div>
                {!editing && (
                    <button
                        onClick={() => setEditing(true)}
                        className="shrink-0 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md"
                    >
                        Edit
                    </button>
                )}
            </div>

            <button
                onClick={() => setExpanded((v) => !v)}
                className="mt-3 text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
            >
                <svg
                    className={`w-4 h-4 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {expanded ? 'Hide documents' : 'Show documents'}
            </button>

            {expanded && <AssociatedDocuments kind={kind} itemId={item.id} />}
        </div>
    );
};

// ---------------------------------------------------------------------------
// One panel: create form + scrollable list.
// ---------------------------------------------------------------------------

const TaxonomyPanel: React.FC<{ kind: TaxonomyKind }> = ({ kind }) => {
    const [items, setItems] = useState<TaxonomyItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreate, setShowCreate] = useState(false);
    const [newSlug, setNewSlug] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [creating, setCreating] = useState(false);

    const labels = KIND_LABEL[kind];

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${apiBase(kind)}/`, { credentials: 'include' });
            if (!response.ok) throw new Error(`status ${response.status}`);
            const data: TaxonomyItem[] = await response.json();
            setItems(Array.isArray(data) ? data : []);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Unknown error';
            setError(msg);
            toast.error(`Failed to load ${labels.plural}`);
        } finally {
            setLoading(false);
        }
    }, [kind, labels.plural]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const handleCreate = async () => {
        const slug = newSlug.trim();
        if (!slug) return;
        setCreating(true);
        try {
            const response = await fetch(`${apiBase(kind)}/create/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: JSON.stringify({ slug, description: newDescription.trim() || null }),
            });
            const data = await response.json();
            if (!response.ok) {
                toast.error(data.error || `Failed to create ${labels.singular}`);
                return;
            }
            const created: TaxonomyItem = data;
            setItems((prev) => {
                const merged = [...prev, created];
                merged.sort((a, b) => a.slug.localeCompare(b.slug));
                return merged;
            });
            setNewSlug('');
            setNewDescription('');
            setShowCreate(false);
            toast.success(`${labels.singular[0].toUpperCase()}${labels.singular.slice(1)} created`);
        } catch {
            toast.error(`Error creating ${labels.singular}`);
        } finally {
            setCreating(false);
        }
    };

    const handleItemUpdated = (updated: TaxonomyItem) => {
        setItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
    };

    return (
        <div className="bg-white rounded-lg shadow flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">{labels.pluralNl}</h3>
                <p className="text-sm text-gray-500 mt-1">
                    Beheer de {labels.pluralNl.toLowerCase()} catalogus. Slugs zijn permanent na aanmaken.
                </p>
            </div>

            <div className="px-6 py-4 flex-1 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                {loading ? (
                    <div className="text-sm text-gray-500">Loading {labels.plural}…</div>
                ) : error ? (
                    <div className="text-sm text-red-600">Failed to load: {error}</div>
                ) : items.length === 0 ? (
                    <div className="text-sm text-gray-500 italic">No {labels.plural} yet.</div>
                ) : (
                    <div className="space-y-3">
                        {items.map((item) => (
                            <TaxonomyRow
                                key={item.id}
                                kind={kind}
                                item={item}
                                onUpdated={handleItemUpdated}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                {showCreate ? (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-700">New {labels.singular}</h4>
                            <button
                                onClick={() => {
                                    setShowCreate(false);
                                    setNewSlug('');
                                    setNewDescription('');
                                }}
                                disabled={creating}
                                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                        <input
                            type="text"
                            value={newSlug}
                            onChange={(e) => setNewSlug(e.target.value)}
                            placeholder="slug (e.g. containment)"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                            autoFocus
                        />
                        <textarea
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            rows={6}
                            placeholder="Description (optional)"
                            className="w-full px-3 py-2 text-sm leading-relaxed border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[8rem]"
                        />
                        <button
                            onClick={handleCreate}
                            disabled={creating || !newSlug.trim()}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {creating ? 'Creating…' : `Add ${labels.singular}`}
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowCreate(true)}
                        className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                    >
                        <span className="text-lg leading-none">+</span>
                        <span>New {labels.singular}</span>
                    </button>
                )}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Top-level component: two panels side-by-side on wide screens.
// ---------------------------------------------------------------------------

const PhasesRolesManager: React.FC = () => {
    return (
        <div>
            <h2 className="text-2xl font-semibold mb-4">Phasen & Rollen</h2>
            <p className="text-sm text-gray-600 mb-6">
                Beheer de phasen en rollen die worden gebruikt om documentatie te taggen en
                simulaties te scopen. Slugs zijn onveranderlijk; alleen de beschrijving kan
                worden bewerkt na aanmaken.
            </p>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <TaxonomyPanel kind="phase" />
                <TaxonomyPanel kind="role" />
            </div>
        </div>
    );
};

export default PhasesRolesManager;
